import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CalendarDays, MapPin, UserRound } from "lucide-react";
import { useFetch, fileUrl, fmtDate, usePageTitle } from "@/lib/api";
import { IMG } from "@/lib/content";
import { PageHero, Loading, EmptyState, Gallery, SectionHeading } from "@/components/site/Bits";

const Panel = ({ title, children, testid }) => (
  <div data-testid={testid}>
    <p className="eyebrow mb-5">{title}</p>
    {children}
  </div>
);

const Roster = ({ roster, isPublic }) => {
  if (!isPublic) return <EmptyState testid="team-roster-private" title="Roster is private" body="This roster is currently shared only with approved families in the parent portal." />;
  if (!roster.length) return <EmptyState testid="team-roster-empty" title="Roster announced after selection" body="Players are listed after tryouts, with parent or guardian permission." />;
  return (
    <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4" data-testid="team-roster">
      {roster.map((p) => (
        <div key={p.id} className="group relative overflow-hidden bg-ink border border-line hover:border-gold transition-colors" data-testid={`roster-player-${p.id}`}>
          <div className="relative aspect-square bg-navy">
            {p.photo_url ? <img src={fileUrl(p.photo_url)} alt={p.name} className="h-full w-full object-cover" /> : <div className="absolute inset-0 stripes" />}
            <span className="absolute left-3 top-2 font-display text-5xl font-black italic text-gold drop-shadow">{p.jersey ? `#${p.jersey}` : ""}</span>
          </div>
          <div className="p-4">
            <p className="font-display text-lg font-extrabold uppercase italic leading-tight">{p.name}</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{p.position}{p.home_org ? ` • ${p.home_org}` : ""}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

const Coach = ({ team }) => (
  <div className="grid gap-6 sm:grid-cols-[180px_1fr] card-navy p-6" data-testid="team-head-coach">
    <div className="aspect-square bg-pitch overflow-hidden">
      {team.head_coach_photo ? <img src={fileUrl(team.head_coach_photo)} alt={team.head_coach} className="h-full w-full object-cover" /> : <div className="h-full w-full stripes flex items-center justify-center"><UserRound className="h-16 w-16 text-line" /></div>}
    </div>
    <div>
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Head Coach • {team.name}</p>
      <p className="mt-1 font-display text-4xl font-black uppercase italic">{team.head_coach || "TBA"}</p>
      {team.head_coach_bio && <p className="mt-3 text-sm text-slate-300 whitespace-pre-line">{team.head_coach_bio}</p>}
      {(team.assistants || []).length > 0 && (
        <div className="mt-5" data-testid="team-assistants">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-2">Assistant Coaching Staff</p>
          <ul className="flex flex-wrap gap-2">
            {team.assistants.map((a, i) => <li key={i} className="border border-line px-3 py-1.5 text-sm"><strong>{a.name}</strong>{a.role ? <span className="text-slate-400"> — {a.role}</span> : null}</li>)}
          </ul>
        </div>
      )}
    </div>
  </div>
);

export default function TeamDetail() {
  const { slug } = useParams();
  const { data, loading, error } = useFetch(`/teams/${slug}`);
  usePageTitle(data ? `${data.team.name} Team` : "Team");
  if (loading) return <div className="wrap pt-40"><Loading /></div>;
  if (error || !data) return <div className="wrap pt-40 pb-20"><EmptyState title="Team not found" testid="team-not-found" /></div>;
  const { team, roster } = data;
  return (
    <div data-testid="team-detail-page">
      <PageHero testid="team-hero" eyebrow={`Tri County Allstars • 2027`} image={team.team_photo ? fileUrl(team.team_photo) : IMG.night}
        title={<><span className="text-gold">{team.name}</span> Allstars</>} sub={`Head Coach: ${team.head_coach || "TBA"}`}>
        <Link to="/teams" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-300 hover:text-gold" data-testid="team-back-link"><ArrowLeft className="h-4 w-4" /> All Teams</Link>
      </PageHero>
      <section className="bg-pitch py-16 sm:py-20">
        <div className="wrap space-y-16">
          <Coach team={team} />
          <div>
            <SectionHeading eyebrow="Player Roster" title="The Roster" />
            <div className="mt-8"><Roster roster={roster} isPublic={team.roster_public} /></div>
          </div>
          <div className="grid gap-12 lg:grid-cols-2">
            <Panel title="Team Schedule" testid="team-schedule">
              {(team.schedule || []).length ? (
                <div className="space-y-px bg-line/60">
                  {team.schedule.map((g, i) => (
                    <div key={i} className="bg-ink p-4 flex items-center gap-4">
                      <CalendarDays className="h-5 w-5 text-gold shrink-0" />
                      <div className="min-w-0">
                        <p className="font-display text-lg font-extrabold uppercase italic">{g.opponent || "TBA"}</p>
                        <p className="text-xs text-slate-400">{fmtDate(g.date)} {g.time ? `• ${g.time}` : ""} {g.location ? `• ${g.location}` : ""}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-slate-400">Schedule will be posted once confirmed.</p>}
            </Panel>
            <Panel title="Practice Information" testid="team-practice">
              <div className="card-navy p-5 flex gap-3"><MapPin className="h-5 w-5 text-gold shrink-0" /><p className="text-sm text-slate-300 whitespace-pre-line">{team.practice_info || "Practice details coming soon."}</p></div>
            </Panel>
          </div>
          <Panel title="Tournament Results" testid="team-results">
            {(team.results || []).length ? (
              <div className="grid gap-3 sm:grid-cols-2">{team.results.map((r, i) => <div key={i} className="card-navy p-5"><p className="font-display text-lg font-extrabold uppercase italic">{r.event}</p><p className="text-gold font-display text-2xl font-black">{r.result}</p></div>)}</div>
            ) : <p className="text-sm text-slate-400">Results will be posted after tournament play.</p>}
          </Panel>
          <Panel title="Team Photo Gallery" testid="team-gallery-panel">
            {(team.gallery || []).length ? <Gallery images={team.gallery} testid="team-gallery" /> : <p className="text-sm text-slate-400">Team photos coming soon.</p>}
          </Panel>
        </div>
      </section>
    </div>
  );
}
